import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { useAuth } from "@/app/providers/AuthProvider";
import { chatLauncherHiddenOn } from "../lib/chat-launcher-visibility";
import { ChatDockContext, type ChatDockOpenRequest, type ChatDockTarget } from "./chat-dock-context";

/**
 * KÖŞEDEKİ SOHBET YUVASINI HER EKRANDAN AÇABİLMEK — denetim Sohbet 18.
 *
 * NEDEN. Rehberin «Sohbet başlat» düğmesi `/chat?with=` adresine GİDİYORDU ve o sayfa konuşmayı
 * anında açıyordu (`POST /chat/conversations`): kişi okuduğu rehberden koparılıyor, hiçbir şey
 * yazmadan vazgeçse bile karşı tarafın listesine boş bir konuşma düşüyordu (sunucu iki tarafa da
 * `created` yayınlıyor). Artık rehber yuvayı YERİNDE, taslakla açıyor; konuşma ilk mesajla oluşuyor.
 *
 * İSTEK BİR BAĞLAM, OLAY DEĞİL. Açan (rehber çekmecesi, rayın canlı yardım düğmesi) ile yuva
 * (`ChatLauncherHost`) aynı kabuğun altında; modül düzeyinde bir olay kanalı, testte temizlenmesi
 * gereken ve iki sekmede farklı davranan bir durum olurdu.
 *
 * AMA İSTEK TEK KULLANIMLIK. Yuva işlediği isteği `consume` ile siliyor; kimlik değişince bekleyen
 * istek de siliniyor. İkisi de yoksa eski istek sağlayıcıda kalıyordu: kapatılan yuva, sohbet
 * sayfasına gidip dönünce kendiliğinden yeniden açılıyordu, destek oturumuna geçen yönetici de
 * bir önceki kimliğin istediği konuşmayla karşılaşabilirdi (bağımsız inceleme).
 *
 * YUVANIN ÇİZİLMEDİĞİ ADRESTE (sohbet sayfası; belge düzenleyici — `chatLauncherHiddenOn`) aynı
 * istek sohbet sayfasının adresine çevriliyor: `?with=<kişi>` (sayfa da taslak açıyor),
 * `/chat/<konuşma>` ya da `?panel=new`. Belge düzenleyicide bu, rayın «Canlı yardım»ının ölü bir
 * düğme olmaması demek; sayfadan çıkışta Collabora belgeyi kendisi kaydediyor.
 */
export function ChatDockProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<ChatDockOpenRequest | null>(null);
  const sequence = useRef(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { session } = useAuth();
  const noDockHere = chatLauncherHiddenOn(pathname);

  const openDock = useCallback(
    (target: ChatDockTarget = {}) => {
      if (noDockHere) {
        if (target.conversationId) void navigate(`/chat/${target.conversationId}`);
        else if (target.draftUserId) {
          void navigate({ pathname: "/chat", search: new URLSearchParams({ with: target.draftUserId }).toString() });
        } else void navigate({ pathname: "/chat", search: "panel=new" });
        return;
      }
      sequence.current += 1;
      setRequest({ ...target, nonce: sequence.current });
    },
    [navigate, noDockHere],
  );

  const consume = useCallback((nonce: number) => {
    setRequest((current) => (current?.nonce === nonce ? null : current));
  }, []);

  /** Kimlik değişti: bir önceki kişinin isteği yenisine taşınmaz. */
  const identityId = session?.user.id ?? null;
  const previousIdentity = useRef(identityId);
  useEffect(() => {
    const before = previousIdentity.current;
    previousIdentity.current = identityId;
    if (before !== identityId) setRequest(null);
  }, [identityId]);

  const value = useMemo(() => ({ request, openDock, consume }), [request, openDock, consume]);
  return <ChatDockContext.Provider value={value}>{children}</ChatDockContext.Provider>;
}
